/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useRef, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, useWindowDimensions, View, type ScrollView } from 'react-native';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import type { StockRow } from '../../inventory/stock/public-api';
import { ModalShell } from '../../shared/modals/ModalShell'
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { currencyFormatter, formatStockQuantity } from '../application/inventoryUtils';

type Props = {
  visible: boolean;
  stock: StockRow | null;
  costInput: string;
  saving: boolean;
  onCostInputChange: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
};

export const CorrectCostModal = ({
  visible,
  stock,
  costInput,
  saving,
  onCostInputChange,
  onClose,
  onSave,
}: Props) => {
  const scrollRef = useRef<ScrollView>(null);
  const contentRef = useRef<View>(null);
  const { width: winW } = useWindowDimensions();
  const isMobile = winW < CONTENT_WIDE_BREAKPOINT;
  const { confirm: confirmDiscard, ConfirmModal: DiscardConfirmModal } = useOperationalConfirm();

  // Baseline al abrir (ajuste de estado en render — evita setState en efecto):
  // si hay un costo cargado y el usuario cierra, se confirma el descarte
  // (mismo patrón que el form de catálogo).
  const [baselineCost, setBaselineCost] = useState('');
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [wasVisible, setWasVisible] = useState(false);
  if (visible !== wasVisible) {
    setWasVisible(visible);
    if (visible) {
      setBaselineCost(costInput);
      setDiscardConfirmOpen(false);
    }
  }

  const isDirty = costInput !== baselineCost && costInput.trim() !== '';
  const closeLocked = saving;
  const guardedClose = useCallback(() => {
    if (closeLocked) return;
    // Si el confirm ya está arriba, Escape/back lo cierra a él.
    if (discardConfirmOpen) return;
    if (!isDirty) {
      onClose();
      return;
    }
    setDiscardConfirmOpen(true);
    void confirmDiscard({
      title: 'Descartar cambios',
      message: 'Hay un costo cargado sin guardar. Si cerrás, se pierde.',
      confirmLabel: 'Descartar',
      destructive: true,
      iconName: 'trash-outline',
    }).then((confirmed) => {
      setDiscardConfirmOpen(false);
      if (confirmed) onClose();
    });
  }, [closeLocked, discardConfirmOpen, isDirty, onClose, confirmDiscard]);

  const qty = stock?.quantity ?? 0;
  const currentCost = stock?.avgCost ?? 0;
  const nextCost = parseDecimalInput(costInput, { min: 0 });
  const currentValue = qty * currentCost;
  const nextValue = nextCost != null ? qty * nextCost : null;
  // $0 pisa la valuación a cero — bloquear también desde el comando.
  const costInvalid = costInput.trim() !== '' && (nextCost === null || nextCost <= 0);
  const saveDisabled = saving || nextCost === null || nextCost <= 0;

  const footerActionsStyle = isMobile ? styles.footerActionsStack : styles.footerActions;
  const btnSecondaryStyle = isMobile ? styles.mobileBtnSecondary : [posUi.secondaryButton, styles.btnSecondary];
  const btnPrimaryStyle = isMobile ? styles.mobileBtnPrimary : [posUi.primaryButton, styles.btnPrimary];
  const btnSecondaryTextStyle = isMobile ? styles.mobileBtnSecondaryText : posUi.secondaryButtonText;
  const btnPrimaryTextStyle = isMobile ? styles.mobileBtnPrimaryText : posUi.primaryButtonText;

  return (
    <>
    <ModalShell
      visible={visible}
      onClose={guardedClose}
      appearance="ticket"
      kicker="Stock"
      title="Corregir costo"
      subtitle={
        stock?.productName
          ? `${stock.productName} — ajustá el costo promedio del depósito`
          : 'Ajustá el costo promedio del depósito'
      }
      widthVariant="standard"
      scrollRef={scrollRef}
      contentRef={contentRef}
      scrollBottomInset={spacing.sm}
      scrollPadding={spacing.md}
      scrollContentStyle={modalExperience.formStack}
      footer={
        <View style={footerActionsStyle}>
          <TouchableOpacity
            style={[btnSecondaryStyle, saving && posUi.disabledCard]}
            onPress={guardedClose}
            disabled={saving}
            accessibilityRole="button"
            accessibilityLabel="Cancelar"
          >
            <Text style={btnSecondaryTextStyle}>Cancelar</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[btnPrimaryStyle, saveDisabled && posUi.disabledCard]}
            onPress={onSave}
            disabled={saveDisabled}
            accessibilityRole="button"
            accessibilityLabel={saving ? 'Guardando…' : 'Guardar costo'}
          >
            <Text style={btnPrimaryTextStyle}>{saving ? 'Guardando…' : 'Guardar costo'}</Text>
          </TouchableOpacity>
        </View>
      }
    >
      <ModalFormField label="Costo unitario" required>
        <NumericTextInput
          kind="decimalMoney"
          style={modalExperience.formInput}
          value={costInput}
          onChangeText={onCostInputChange}
          placeholder="Ej: 120 o 120,50"
          placeholderTextColor={ticketColors.ink3}
        />
        <Text style={posUi.checkoutHint}>
          Es el precio al que lo comprás, no el de venta. Queda como costo promedio de lo que hay en esta sucursal.
        </Text>
        {costInvalid ? (
          <Text style={[posUi.checkoutHint, styles.fieldError]}>El costo tiene que ser mayor a $0.</Text>
        ) : null}
      </ModalFormField>
      {stock ? (
        <View>
          <View style={modalExperience.detailKvRow}>
            <Text style={modalExperience.detailMetaLabel}>Cantidad</Text>
            <Text style={modalExperience.detailKvValueStrong}>
              {formatStockQuantity(qty, stock.unitOfMeasure)}
            </Text>
          </View>
          <View style={modalExperience.detailKvRow}>
            <Text style={modalExperience.detailMetaLabel}>Costo actual</Text>
            <Text style={modalExperience.detailKvValueStrong}>{currencyFormatter.format(currentCost)}</Text>
          </View>
          <View style={modalExperience.detailKvRow}>
            <Text style={modalExperience.detailMetaLabel}>Valuación actual</Text>
            <Text style={modalExperience.detailKvValueStrong}>{currencyFormatter.format(currentValue)}</Text>
          </View>
          <View style={modalExperience.detailKvRow}>
            <Text style={modalExperience.detailMetaLabel}>Valuación nueva</Text>
            <Text style={modalExperience.detailKvValueStrong}>
              {nextValue != null ? currencyFormatter.format(nextValue) : '—'}
            </Text>
          </View>
        </View>
      ) : null}
    </ModalShell>
    {DiscardConfirmModal}
    </>
  );
};

const styles = StyleSheet.create({
  fieldError: {
    color: ticketColors.bad,
  },
  footerActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    width: '100%',
  },
  footerActionsStack: {
    flexDirection: 'column',
    gap: spacing.sm,
    width: '100%',
  },
  btnSecondary: {
    flex: 1,
    paddingHorizontal: spacing.md,
    minHeight: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  btnPrimary: {
    flex: 1,
    minHeight: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileBtnSecondary: {
    minHeight: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    width: '100%',
  },
  mobileBtnSecondaryText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  mobileBtnPrimary: {
    minHeight: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    width: '100%',
  },
  mobileBtnPrimaryText: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.goldInk,
  },
});
